import { DatePipe } from '@angular/common';
import {
    Component,
    inject,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { firstValueFrom, forkJoin, Subscription, take } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
    provideTranslocoScope,
    translate,
    TranslocoService,
} from '@jsverse/transloco';
import { HippoConfirmationService } from '@hippo/services/confirmation';
import { MaintenanceService } from './maintenance.service';
import {
    ReactivateDialogComponent,
    ReactivateDialogResult,
} from './reactivate-dialog/reactivate-dialog.component';

@Component({
    selector: 'app-maintenance',
    standalone: true,
    templateUrl: './maintenance.component.html',
    styleUrls: ['./maintenance.component.scss'],
    imports: [DatePipe, MatIconModule],
    changeDetection: ChangeDetectionStrategy.Default,
    providers: [provideTranslocoScope('codext')],
})
export class MaintenanceComponent implements OnInit, OnDestroy {
    maintenanceService = inject(MaintenanceService);
    private dialog = inject(MatDialog);
    private httpClient = inject(HttpClient);
    private confirmationService = inject(HippoConfirmationService);
    private translocoService = inject(TranslocoService);
    private langChangeSub: Subscription;
    private lastTap = 0;

    async ngOnInit(): Promise<void> {
        this.maintenanceService.startPolling();
        this.maintenanceService.startVisibilityCheck();

        await firstValueFrom(
            forkJoin([
                this.translocoService.selectTranslation('codext').pipe(take(1)),
            ])
        );
        this.langChangeSub = this.translocoService.langChanges$.subscribe(
            async () => {
                await firstValueFrom(
                    forkJoin([
                        this.translocoService
                            .selectTranslation('codext')
                            .pipe(take(1)),
                    ])
                );
            }
        );
    }

    onHotspotTouch(): void {
        const now = Date.now();
        if (now - this.lastTap < 300) {
            this.reActivate();
        }
        this.lastTap = now;
    }

    async reActivate(): Promise<void> {
        const ref = this.dialog.open(ReactivateDialogComponent, {
            disableClose: true,
            panelClass: 'maintenance-dialog',
        });
        const result: ReactivateDialogResult | undefined = await firstValueFrom(
            ref.afterClosed()
        );
        if (!result) {
            return;
        }
        try {
            const formData = new FormData();
            formData.append('submit_option', 'opt_reactivate');
            formData.append('initials', result.initials);
            formData.append('password', result.password);

            await firstValueFrom(
                this.httpClient.post<any>(
                    'hippo/navigator.php?page=pages/system/System_settings.php&page_type=json',
                    formData,
                    { observe: 'response' }
                )
            );
            this.maintenanceService.checkMaintenance();
        } catch (error) {
            console.log(error);
            this.confirmationService.open({
                title: 'Wartung deaktivieren &nbsp;/&nbsp; Deactivate Maintenance',
                message:
                    'Die Wartung konnte nicht deaktiviert werden. Möglicherweise verfügen Sie nicht über die erforderlichen Berechtigungen.' +
                    '<br><br>' +
                    'Maintenance could not be deactivated. You may not have the required permissions to perform this action.',
                icon: { show: true, color: 'warn' },
                actions: {
                    confirm: { label: 'OK', color: 'primary' },
                    cancel: { show: false },
                },
                dismissible: false,
                panelClass: 'maintenance-confirmation-dialog',
            });
        }
    }

    ngOnDestroy(): void {
        this.maintenanceService.stopPolling();
        this.maintenanceService.stopVisibilityCheck();
        this.langChangeSub?.unsubscribe();
    }
}
